import { type ReactNode, useCallback, useEffect, useState } from 'react'
import { api, type ApiToken } from '../api'
import { Icon } from './Icon'

/**
 * Tokens a workspace mints for its own agents and scripts.
 *
 * This screen used to say "ask your admin for a service token", which is a
 * product admitting it has no answer: the only credential was one the
 * deployment was started with, shared by every workspace and never expiring.
 *
 * The secret is shown ONCE, and the copy is offered while it is on screen.
 * There is no "show it again" because a token a product can show twice is a
 * token the product is keeping, which is the property the whole feature
 * exists to avoid — so the panel says so plainly rather than letting somebody
 * discover it by closing the dialog.
 */
const SCOPE_HELP: Record<string, string> = {
  'docs:read': 'Read pages and drafts',
  'docs:write': 'Stage edits on a draft',
  'docs:commit': 'Commit a draft and open a review',
}

/**
 * What a token cannot do, said where somebody is deciding to mint one.
 *
 * The three scopes are all about documentation, and for a while the
 * enforcement was not: `docs:write` reached every route that was not a GET, so
 * a token minted for a changelog agent could rename the workspace, make a
 * project, read the audit trail and delete the workspace. The API draws the
 * line now, and a person handing a token to an agent should be able to read
 * where it is without testing it.
 */
const NEVER = 'Publishing, the workspace, its people and who may read the site stay with a person.'

export function AgentTokens({
  mayManage,
  renderMinted,
}: {
  mayManage: boolean
  renderMinted?: (token: string, dismiss: () => void) => ReactNode
}) {
  const [tokens, setTokens] = useState<ApiToken[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [name, setName] = useState('')
  const [days, setDays] = useState('90')
  const [scopes, setScopes] = useState<string[]>(['docs:read'])
  const [minted, setMinted] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const load = useCallback(async () => {
    try {
      setTokens((await api.tokens()).tokens)
      setError(null)
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    }
  }, [])
  useEffect(() => {
    if (mayManage) void load()
  }, [load, mayManage])

  if (!mayManage) {
    return (
      <p className="home__quiet">
        An owner or an admin can mint a token for an agent. Ask one of them, and they can scope it
        to reading only.
      </p>
    )
  }

  const mint = async () => {
    setBusy(true)
    setError(null)
    try {
      const made = await api.mintToken({ name: name.trim(), scopes, days: Number(days) })
      setMinted(made.token)
      setName('')
      await load()
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="tokens">
      {minted && (
        <>
          {renderMinted ? (
            renderMinted(minted, () => setMinted(null))
          ) : (
            <div className="tokens__new">
              <p>
                <strong>Copy this now.</strong> It is shown once and never again — the product keeps
                only a hash of it, which is the point.
              </p>
              <code className="tokens__secret">{minted}</code>
              <div className="screen__actions">
                <button
                  className="btn btn--primary btn--sm"
                  onClick={() => void navigator.clipboard?.writeText(minted).catch(() => undefined)}
                >
                  <Icon name="check" size={13} /> Copy
                </button>
                <button className="btn btn--sm" onClick={() => setMinted(null)}>
                  Done
                </button>
              </div>
            </div>
          )}
        </>
      )}

      <div className="tokens__mint">
        <input
          className="input"
          value={name}
          placeholder="What it is for — “CI”, “changelog bot”"
          disabled={busy}
          onChange={(e) => setName(e.target.value)}
        />
        <div className="tokens__scopes">
          {Object.entries(SCOPE_HELP).map(([scope, help]) => (
            <label key={scope} className="tokens__scope">
              <input
                type="checkbox"
                checked={scopes.includes(scope)}
                disabled={busy}
                onChange={(e) =>
                  setScopes((was) =>
                    e.target.checked ? [...was, scope] : was.filter((s) => s !== scope),
                  )
                }
              />
              <code>{scope}</code>
              {/* What the scope allows, beside it rather than in a `title` on
                  this label — which reached no keyboard, no touch, and not the
                  checkbox inside it either. Somebody is deciding what a
                  machine may do to their documentation; the answer belongs on
                  the screen while they decide. */}
              <span className="tokens__scope-help">{help}</span>
            </label>
          ))}
        </div>
        <label className="tokens__days">
          Expires in
          <input
            className="input"
            value={days}
            inputMode="numeric"
            disabled={busy}
            onChange={(e) => setDays(e.target.value)}
          />
          days
        </label>
        {/*
          Tinted while a secret is on screen, filled the rest of the time.

          Minting leaves the form standing, so for as long as the token is
          shown there were two filled accents in one dialog — Copy, and Mint
          another. BRAND's rule is one, and here the reason for it is literal:
          the secret is shown ONCE, the product keeps only a hash, and the
          only act that matters in that moment is taking a copy of it.
          Minting again is still offered, at the volume of something that can
          wait.
        */}
        <button
          className={`btn ${minted ? 'btn--tinted' : 'btn--primary'}`}
          disabled={busy || name.trim() === '' || scopes.length === 0}
          onClick={() => void mint()}
        >
          {busy ? <Icon name="spinner" size={13} /> : null} Mint a token
        </button>
      </div>
      <p className="home__quiet">{NEVER}</p>

      {error && (
        <p className="screen__error">
          <Icon name="alert" size={13} /> {error}
        </p>
      )}

      {/* The list is read when the dialog opens, and before this the space
          under the mint form was simply empty until it came back — which is
          the same picture as "you have no tokens", drawn without knowing. */}
      {tokens === null && !error && (
        <div className="screen__loading" aria-busy="true">
          <p className="sr-only">Loading…</p>
          <span className="skeleton" />
          <span className="skeleton" />
          <span className="skeleton" />
        </div>
      )}

      {tokens && tokens.length === 0 && (
        /* Without {NEVER}: it is already printed above, under the mint form it
           belongs to, so an empty list said the same sentence twice on the
           same screen. */
        <p className="nothing">
          No tokens yet. Mint one above and give it to the agent — scoped to reading only unless it
          needs to write.
        </p>
      )}

      {tokens && tokens.length > 0 && (
        <ul className="tokens__list">
          {tokens.map((token) => {
            const dead = Boolean(token.revokedAt) || new Date(token.expiresAt) <= new Date()
            return (
              <li key={token.id} className={`tokens__row${dead ? ' is-dead' : ''}`}>
                <span className="tokens__body">
                  <span className="tokens__name">
                    {token.name}
                    <code className="tokens__prefix">{token.prefix}…</code>
                  </span>
                  <span className="tokens__meta">
                    {token.scopes.join(' · ')}
                    {' — '}
                    {token.revokedAt
                      ? 'revoked'
                      : new Date(token.expiresAt) <= new Date()
                        ? 'expired'
                        : `expires ${new Date(token.expiresAt).toLocaleDateString()}`}
                    {token.lastUsedAt
                      ? ` · last used ${new Date(token.lastUsedAt).toLocaleDateString()}`
                      : ' · never used'}
                  </span>
                </span>
                {!dead && (
                  <button
                    className="btn btn--ghost btn--sm"
                    onClick={() =>
                      void api
                        .revokeToken(token.id)
                        .then(load)
                        .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)))
                    }
                  >
                    Revoke
                  </button>
                )}
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}
